import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Paper, 
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Alert
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';

const ChecklistDetail = () => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const { user } = useAuth();

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        setLoading(true);
        const data = await apiService.getChecklistById(id);
        setChecklist(data);
      } catch (err) {
        console.error('Error fetching checklist:', err);
        setError('Failed to fetch checklist details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchChecklist();
    } else {
      setLoading(false);
    }
  }, [id, user]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  if (loading) {
    return <Box display="flex" justifyContent="center"><CircularProgress /></Box>;
  }

  if (!user) {
    return <Typography>Please log in to view checklist details.</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!checklist) {
    return <Typography>No checklist found</Typography>;
  }

  const renderSection = (title, fields, mdWidth = 6) => (
    <Grid item xs={12} md={mdWidth}>
      <Box mb={2}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>{title}</Typography>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableBody>
              {fields.map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '50%', border: 'none' }}>{label}</TableCell>
                  <TableCell align="left" sx={{ border: 'none' }}>
                    {typeof value === 'boolean'
                      ? (value ? 'Yes' : 'No')
                      : (label.toLowerCase().includes('date') || label.toLowerCase().includes('expiry')
                        ? formatDate(value)
                        : (value || 'N/A'))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Grid>
  );

  return (
    <Paper elevation={3} sx={{ p: 4, backgroundColor: '#f5f5f5' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#1976d2', textAlign: 'center' }}>
        Vehicle Maintenance Checklist
      </Typography>
      <Grid container spacing={3}>
        {renderSection('General Information', [
          ['Date', checklist.date],
         ['Unit Number', checklist.vehicle_number],
          ['Driver', checklist.driver],
          ['License Plate Number', checklist.license_plate_number],
          ['Current Odometer', checklist.current_odometer],
          ['Previous Odometer', checklist.previous_odometer],
          ['Miles Traveled', checklist.current_odometer - checklist.previous_odometer]
        ])}
        {renderSection('Fluid Levels', [
          ['Oil Level', checklist.oil_level],
          ['Oil Level Notes', checklist.oil_level_notes],
          ['Last Oil Change Date', checklist.oil_change_last_date],
          ['Next Oil Change Date', checklist.oil_change_next_date],
          ['Antifreeze Level', checklist.antifreeze_level],
          ['Windshield Fluid', checklist.windshield_fluid],
          ['Transmission Fluid', checklist.transmission_fluid],
          ['Brake Fluid', checklist.brake_fluid]
        ])}
        {renderSection('Tire Condition', [
          ['Tire Pressure LF', checklist.tire_pressure_lf],
          ['Tire Pressure RF', checklist.tire_pressure_rf],
          ['Tire Pressure LR', checklist.tire_pressure_lr],
          ['Tire Pressure RR', checklist.tire_pressure_rr],
          ['Tire Tread', checklist.tire_tread]
        ])}
        {renderSection('Vehicle Systems', [
          ['Fan Belt', checklist.fan_belt],
          ['Headlights', checklist.headlights],
          ['Taillights', checklist.taillights],
          ['Brake Lights', checklist.brake_lights],
          ['Running Lights', checklist.running_lights],
          ['Turn Signals', checklist.turn_signals],
          ['Emergency Flashers', checklist.emergency_flashers]
        ])}
        {renderSection('Documentation', [
          ['Registration', checklist.registration],
          ['Registration Expiry', checklist.registration_expiry],
          ['Insurance', checklist.insurance]
        ], 4)}
        {renderSection('Maintenance Dates', [
          ['Tire Rotation Date', checklist.tire_rotation_date],
          ['Engine Tuneup Date', checklist.engine_tuneup_date],
          ['Transmission Service Date', checklist.transmission_service_date]
        ], 4)}
        {renderSection('Vehicle Condition', [
          ['Appearance', checklist.appearance],
          ['Exterior Condition', checklist.exterior_condition],
          ['Interior Vacuumed', checklist.interior_vacuumed],
          ['Interior Condition', checklist.interior_condition]
        ], 4)}
        {renderSection('Safety Equipment', [
          ['Fire Extinguisher', checklist.fire_extinguisher],
          ['Fire Extinguisher Charged', checklist.fire_extinguisher_charged],
          ['Fire Extinguisher Cert Date', checklist.fire_extinguisher_cert_date],
          ['Safety Kit Flags', checklist.safety_kit_flags],
          ['Safety Kit First Aid', checklist.safety_kit_first_aid],
          ['Safety Kit Eye Wash', checklist.safety_kit_eye_wash],
          ['Safety Kit Eye Wash Expiry', checklist.safety_kit_eye_wash_expiry],
          ['Safety Kit Flares', checklist.safety_kit_flares],
          ['Safety Kit Gloves', checklist.safety_kit_gloves],
          ['Safety Kit Hard Hat', checklist.safety_kit_hard_hat],
          ['Safety Goggles', checklist.safety_kit_goggles],
          ['Safety Kit Caution Tape', checklist.safety_kit_caution_tape],
          ['Safety Kit Cones', checklist.safety_kit_cones],
          ['Safety Kit Harness', checklist.safety_kit_harness],
          ['Safety Kit Lumbar Support', checklist.safety_kit_lumbar_support]
        ], 8)}
        {renderSection('Additional Items', [
          ['Sanitizing Supplies', checklist.sanitizing_supplies],
          ['Extension Cords', checklist.extension_cords],
          ['Tire Traction Devices', checklist.tire_traction_devices]
        ], 4)}
        {renderSection('Notes and Signature', [
          ['Notes', checklist.notes],
          ['Technician Signature', checklist.tech_signature]
        ], 12)}
      </Grid>
    </Paper>
  );
};

export default ChecklistDetail;